import * as React from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { parseExpiryDeadlineAuditDetails } from '../lib/expiry-deadline-audit';
import { isDeadlineEditEligible } from '../lib/formatters';
import { ExpiryDeadlineAuditDetails } from './expiry-deadline-audit-details';
import type {
  UnitAuditEntry,
  UnitReservationRow,
  UnitReservationsPermissions,
  UnitReservationsTabLabels,
} from './unit-reservations-tab-types';

// ─── Row actions ──────────────────────────────────────────────
// Always includes "Otwórz ofertę" when offer exists.
// Gated transition items follow after a separator.

export type TabRowAction = {
  label: string;
  action: string;
  href?: string;
  destructive?: boolean;
  separator?: boolean;
};

// eslint-disable-next-line complexity -- business rule fan-out; every status/role combination is a separate branch
export function buildRowActions(
  item: UnitReservationRow,
  permissions: UnitReservationsPermissions,
  currentUserId: number,
  labels: UnitReservationsTabLabels,
): TabRowAction[] {
  const actions: TabRowAction[] = [];

  // Universal: open the offer workspace when this row has an offer
  if (item.offerId !== null && item.offerHref !== null) {
    actions.push({ label: labels.openOfferLabel, action: 'open_offer', href: item.offerHref });
  }

  const isOwn = item.holderUserId === currentUserId;
  const canAct = isOwn || permissions.canForceRelease;
  let separatorAdded = false;

  const addGated = (action: TabRowAction) => {
    if (!separatorAdded) {
      actions.push({ ...action, separator: true });
      separatorAdded = true;
    } else {
      actions.push(action);
    }
  };

  if (canAct) {
    if (item.status === 'SOFT_RESERVATION') {
      // close_sale is valid from SOFT_RESERVATION per spec (CAS transition path)
      if (permissions.canUpgradeHard)
        addGated({ label: labels.upgradeHardLabel, action: 'upgrade_hard' });
      if (permissions.canCloseSale)
        addGated({ label: labels.closeSaleLabel, action: 'close_sale' });
      if (permissions.canUpdateDates)
        addGated({ label: labels.editDatesLabel, action: 'edit_dates' });
      if (permissions.canCancel)
        addGated({ label: labels.cancelLabel, action: 'cancel', destructive: true });
    } else if (item.status === 'HARD_RESERVATION') {
      if (permissions.canCloseSale)
        addGated({ label: labels.closeSaleLabel, action: 'close_sale' });
      if (permissions.canUpdateDates)
        addGated({ label: labels.editDatesLabel, action: 'edit_dates' });
      if (permissions.canCancel)
        addGated({ label: labels.cancelLabel, action: 'cancel', destructive: true });
    } else if (item.status === 'OPTION') {
      if (permissions.canUpdateDates)
        addGated({ label: labels.editDatesLabel, action: 'edit_dates' });
      if (permissions.canCancel)
        addGated({ label: labels.cancelLabel, action: 'cancel', destructive: true });
    } else if (item.status === 'SOLD') {
      if (permissions.isAdmin && permissions.canUpdateDates)
        addGated({ label: labels.editDatesLabel, action: 'edit_dates' });
      if (permissions.isAdmin && permissions.canCancel)
        addGated({ label: labels.cancelLabel, action: 'cancel', destructive: true });
    }
  }

  if (permissions.isAdmin && permissions.canOverrideStatus)
    addGated({ label: labels.overrideStatusLabel, action: 'override_status' });
  if (permissions.isAdmin && permissions.canForceRelease && !isOwn)
    addGated({ label: labels.forceReleaseLabel, action: 'force_release', destructive: true });

  // Permission-only, independent of ownership (CU-86cb7qgc9) — unlike the
  // status-branch actions above, reservation.extend alone is the gate.
  // `?? false`/`?? null` fail closed for callers that predate this field
  // (e.g. apps/mockups) instead of throwing on an undefined permission.
  if (
    (permissions.canExtendExpiry ?? false) &&
    isDeadlineEditEligible(item.status, item.effectiveExpiresAt ?? null)
  ) {
    addGated({ label: labels.extendExpiryLabel ?? '', action: 'expiry_deadline' });
  }

  // Empty array (e.g. no offer, no permissions) means the row has no actions at all —
  // the caller (UnitReservationsTab) renders no trigger/menu for that row.
  return actions;
}

// ─── Audit dot color ──────────────────────────────────────────

// Shared class for the sticky right-hand actions column (header and body cells)
export const STICKY_COL_SHADOW =
  'border-l border-border/40 shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.08)]';

function auditDotColor(action: string): string {
  if (action.includes('created')) return 'bg-blue-400';
  if (action.includes('upgraded_hard')) return 'bg-amber-400';
  if (action.includes('closed_sale')) return 'bg-green-500';
  if (action.includes('cancelled') || action.includes('force_released')) return 'bg-red-500';
  if (action.includes('override')) return 'bg-red-400';
  if (action.includes('auto_extension') || action.includes('auto_promotion')) return 'bg-primary';
  return 'bg-muted-foreground';
}

// ─── Expandable JSON details ──────────────────────────────────

function AuditEntryDetails({
  details,
  expandLabel,
  collapseLabel,
}: {
  details: Record<string, unknown>;
  expandLabel: string;
  collapseLabel: string;
}) {
  const [expanded, setExpanded] = React.useState(false);
  if (Object.keys(details).length === 0) return null;
  return (
    <div className="mt-1">
      {}
      <button
        type="button"
        className="flex items-center gap-1 text-2xs text-muted-foreground hover:text-foreground"
        onClick={() => setExpanded((v) => !v)}
      >
        {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        {expanded ? collapseLabel : expandLabel}
      </button>
      {expanded ? (
        <pre className="mt-1 text-2xs text-muted-foreground bg-muted/30 rounded p-2 overflow-x-auto whitespace-pre-wrap max-w-sm">
          {JSON.stringify(details, null, 2)}
        </pre>
      ) : null}
    </div>
  );
}

export function UnitAuditTimeline({
  auditEntries,
  labels,
}: {
  auditEntries: UnitAuditEntry[];
  labels: UnitReservationsTabLabels;
}) {
  const [auditExpanded, setAuditExpanded] = React.useState(false);
  return (
    <div data-testid="unit-audit-timeline" className="rounded-lg border border-border/60 bg-card">
      <button
        type="button"
        className="flex w-full items-center justify-between px-4 py-3 text-[13px] font-medium hover:bg-muted/20 transition-colors rounded-lg"
        onClick={() => setAuditExpanded((v) => !v)}
      >
        <span>{labels.auditTimelineTitle}</span>
        <span className="flex items-center gap-1 text-muted-foreground text-xs">
          {auditExpanded ? (
            <>
              {labels.auditTimelineHideLabel}
              <ChevronUp className="h-4 w-4" />
            </>
          ) : (
            <>
              {labels.auditTimelineShowLabel}
              <ChevronDown className="h-4 w-4" />
            </>
          )}
        </span>
      </button>

      {auditExpanded ? (
        <div className="px-4 pb-4 border-t border-border/40">
          {auditEntries.length === 0 ? (
            <p className="pt-4 text-[13px] text-muted-foreground">
              {labels.auditTimelineEmptyLabel}
            </p>
          ) : (
            <div className="relative pt-4">
              <div className="absolute left-[7px] top-4 bottom-0 w-px bg-border/60" />
              <ul className="space-y-4">
                {auditEntries.map((entry) => (
                  <li key={entry.id} className="flex gap-4 relative">
                    <div
                      className={`w-3.5 h-3.5 rounded-full shrink-0 mt-0.5 ${auditDotColor(entry.action)} border-2 border-background z-10`}
                    />
                    <div className="flex-1 -mt-0.5">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-[13px] font-medium text-foreground">
                          {entry.actionLabel}
                        </span>
                        <span className="text-2xs text-muted-foreground">
                          {entry.actorUserName ?? labels.systemActorLabel}
                        </span>
                        <span className="text-2xs text-muted-foreground">·</span>
                        <span className="text-2xs text-muted-foreground">{entry.createdAt}</span>
                      </div>
                      <p className="text-2xs text-muted-foreground mt-0.5">{entry.sourceLabel}</p>
                      {(() => {
                        const parsed = parseExpiryDeadlineAuditDetails(entry.action, entry.details);
                        return parsed ? (
                          <ExpiryDeadlineAuditDetails
                            details={parsed}
                            actorUserName={entry.actorUserName}
                            labels={{
                              // Fallback empty strings: these labels are optional
                              // (backward-compat for callers predating CU-86cb7qgc9)
                              // and only rendered here once a well-formed
                              // expiry_deadline_updated event is actually present.
                              title: labels.expiryDeadlineAuditTitle ?? '',
                              actorLabel: labels.expiryDeadlineAuditActorLabel ?? '',
                              previousLabel: labels.expiryDeadlineAuditPreviousLabel ?? '',
                              newLabel: labels.expiryDeadlineAuditNewLabel ?? '',
                              reasonLabel: labels.expiryDeadlineAuditReasonLabel ?? '',
                              systemActorLabel: labels.systemActorLabel,
                            }}
                          />
                        ) : (
                          <AuditEntryDetails
                            details={entry.details}
                            expandLabel={labels.expandDetailsLabel}
                            collapseLabel={labels.collapseDetailsLabel}
                          />
                        );
                      })()}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
